import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Action, EmptyState, ErrorNote, FilterChips, PageBody, PageHead } from '../../components/aa';
import { useGrow } from '../orbit-room/OverviewCharts';
import { Card, CardSkeleton, Legend, MiniRing, Reveal } from './dashboard/primitives';
import { billingApi, type PersonUsageDetail, type UsagePeriod } from '../../services/billingApi';
import PersonWorkCard from './PersonWorkCard';
import {
  AI_TONE, COMPUTE_TONE, PERIODS, Avatar, displayName, exactUsd, lastSeen, roleLabel, sinceLabel, usd,
} from '../../components/settings/usageParts';

const DAY = 86_400_000;
const MIN_DAYS = 14;
const WEEKLY_AFTER = 62;

const isPeriod = (value: string | null): value is UsagePeriod => PERIODS.some((p) => p.id === value);

const dayStart = (iso: string): number => {
  const d = new Date(iso);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const shortDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });

interface Bucket { start: number; label: string; ai: number; compute: number }

/* Days from the start of the chosen period to today, at least two weeks so a
 * fresh period still reads as a timeline; weeks once it runs past two months. */
const buildSeries = (detail: PersonUsageDetail): { buckets: Bucket[]; weekly: boolean } => {
  const today = dayStart(new Date().toISOString());
  let start = today - (MIN_DAYS - 1) * DAY;
  if (detail.since) start = Math.min(start, dayStart(detail.since));
  if (!detail.since && detail.totals?.firstAt) start = Math.min(start, dayStart(detail.totals.firstAt));
  const days = Math.round((today - start) / DAY) + 1;
  const weekly = days > WEEKLY_AFTER;
  const size = weekly ? 7 : 1;
  const buckets: Bucket[] = Array.from({ length: Math.ceil(days / size) }, (_, i) => {
    const at = start + i * size * DAY;
    return { start: at, label: shortDay(at), ai: 0, compute: 0 };
  });
  (detail.daily || []).forEach((row) => {
    const bucket = buckets[Math.floor((dayStart(`${row.day}T00:00:00Z`) - start) / (size * DAY))];
    if (!bucket) return;
    bucket.ai += row.aiUsd;
    bucket.compute += row.computeUsd;
  });
  return { buckets, weekly };
};

const Figure: React.FC<{ label: string; value: string; note: string; visual?: React.ReactNode }> = ({ label, value, note, visual }) => (
  <div className="flex h-full items-start justify-between gap-3 rounded-[14px] border border-line bg-white px-5 py-4 shadow-[0_1px_2px_rgba(24,25,29,0.04)]">
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-eyebrow">{label}</div>
      <div className="mt-1.5 text-[24px] font-bold leading-[30px] tracking-[-0.01em] text-ink tabular-nums">{value}</div>
      <div className="mt-0.5 text-[12px] text-ink-dim">{note}</div>
    </div>
    {visual && <div className="shrink-0 pt-1">{visual}</div>}
  </div>
);

const UsageOverTime: React.FC<{ detail: PersonUsageDetail }> = ({ detail }) => {
  const t = useGrow(1);
  const [hover, setHover] = useState(-1);
  const { buckets, weekly } = useMemo(() => buildSeries(detail), [detail]);
  const max = Math.max(...buckets.map((b) => b.ai + b.compute), 0.000001);
  const focus = hover >= 0 ? buckets[hover] : null;
  const labelEvery = Math.max(1, Math.ceil(buckets.length / 7));
  let readout = `Hover a ${weekly ? 'week' : 'day'} for its amount`;
  if (focus) readout = `${weekly ? 'Week of ' : ''}${focus.label}: ${exactUsd(focus.ai + focus.compute)}`;

  return (
    <Card title="Usage over time" sub={`Each ${weekly ? 'week' : 'day'}, ${sinceLabel(detail.period, detail.since)}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Legend items={[{ label: 'AI', tone: AI_TONE }, { label: 'Workspace compute', tone: COMPUTE_TONE }]} />
        <span className="min-h-[16px] text-[11px] tabular-nums text-ink-dim" aria-live="polite">{readout}</span>
      </div>
      <div className="relative h-[200px]" onMouseLeave={() => setHover(-1)}>
        {[0.5, 1].map((g) => (
          <span key={g} className="pointer-events-none absolute inset-x-0 border-t border-dashed border-line" style={{ bottom: `${g * 100}%` }}>
            <span className="absolute -top-2 left-0 bg-white pr-1.5 text-[10px] tabular-nums text-ink-soft">{usd(max * g)}</span>
          </span>
        ))}
        <div className="absolute inset-0 grid items-end gap-[3px] pl-12" style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }}>
          {buckets.map((b, i) => {
            const total = b.ai + b.compute;
            return (
              <button
                key={b.start}
                type="button"
                aria-label={`${b.label}: ${exactUsd(total)}`}
                onMouseEnter={() => setHover(i)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(-1)}
                className="flex h-full flex-col justify-end px-[1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              >
                <span
                  className={clsx('mx-auto flex w-full max-w-[18px] flex-col justify-end overflow-hidden rounded-t-[4px] transition-opacity', hover >= 0 && hover !== i && 'opacity-40')}
                  style={{ height: `${(total / max) * 100 * t}%`, minHeight: total > 0 ? 3 : 0 }}
                >
                  <span style={{ height: total ? `${(b.compute / total) * 100}%` : 0, background: COMPUTE_TONE }} />
                  <span style={{ height: total ? `${(b.ai / total) * 100}%` : 0, background: AI_TONE }} />
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-1.5 grid gap-[3px] pl-12" style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }}>
        {buckets.map((b, i) => (
          <span key={b.start} className={clsx('overflow-visible whitespace-nowrap text-center text-[10px] tabular-nums', hover === i ? 'font-semibold text-ink' : 'text-ink-soft')}>
            {(i % labelEvery === 0 || hover === i) && b.label}
          </span>
        ))}
      </div>
    </Card>
  );
};

/* One person's usage, for the Owner: how much, and when. Amounts are what the
 * company is charged for their work. */
const PersonUsagePage: React.FC = () => {
  const navigate = useNavigate();
  const { key = '' } = useParams<{ key: string }>();
  const [search, setSearch] = useSearchParams();
  const requested = search.get('period');
  const period: UsagePeriod = isPeriod(requested) ? requested : 'period';
  const [detail, setDetail] = useState<PersonUsageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    billingApi.getPersonUsage(key, period)
      .then(({ data }) => setDetail(data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [key, period]);

  useEffect(() => { load(); }, [load]);

  const setPeriod = (next: UsagePeriod) => {
    const params = new URLSearchParams(search);
    params.set('period', next);
    setSearch(params, { replace: true });
  };

  const person = detail?.person;
  const totals = detail?.totals;
  let title = 'Usage';
  let sub = '';
  if (person) {
    title = displayName(person);
    sub = roleLabel(person);
  }
  if (person?.kind === 'unlinked') sub = 'Work the agents did on their own, and older calls made before each call recorded a person.';

  const share = totals && detail?.company_total_usd ? totals.totalUsd / detail.company_total_usd : 0;
  const work = detail?.work;
  const hasWork = Boolean(work?.companies.length);

  let body: React.ReactNode = null;
  if (loading && !detail) {
    body = (
      <div aria-busy="true" aria-label="Loading usage" className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => <CardSkeleton key={i} height={112} />)}
        </div>
        <CardSkeleton height={310} />
      </div>
    );
  }
  if (detail && !detail.available) {
    body = <EmptyState title="Usage is being refreshed" reason="The figures could not be read right now. Check back in a minute." action={<Action variant="outline" onClick={load}>Try again</Action>} />;
  }
  if (detail?.available && totals && totals.totalUsd === 0 && totals.calls === 0 && !hasWork) {
    body = (
      <EmptyState
        title={`No usage ${sinceLabel(detail.period, detail.since)}`}
        reason={period === 'all' ? 'Nothing has been recorded for this person yet.' : 'Try a longer period to see earlier work.'}
        action={period !== 'all' ? <Action variant="outline" onClick={() => setPeriod('all')}>Show all time</Action> : undefined}
      />
    );
  }
  if (detail?.available && totals && (totals.totalUsd > 0 || totals.calls > 0 || hasWork)) {
    body = (
      <div className={clsx('space-y-4 transition-opacity', loading && 'opacity-50')}>
        <div className="grid gap-3 sm:grid-cols-3">
          <Reveal order={0}>
            <Figure
              label="Total usage"
              value={usd(totals.totalUsd)}
              note={detail.company_total_usd ? `${Math.round(share * 100)}% of your company's ${usd(detail.company_total_usd)}` : lastSeen(totals.lastAt)}
              visual={detail.company_total_usd ? <MiniRing value={share} label={`${Math.round(share * 100)}% of company usage`} tone={AI_TONE} size={40} /> : undefined}
            />
          </Reveal>
          <Reveal order={1}>
            <Figure label="AI usage" value={usd(totals.aiUsd)} note={lastSeen(totals.lastAt)} />
          </Reveal>
          <Reveal order={2}>
            <Figure label="Workspace compute" value={usd(totals.computeUsd)} note="Cloud workspaces for their projects" />
          </Reveal>
        </div>
        <Reveal order={3}><UsageOverTime detail={detail} /></Reveal>
        {work && hasWork && (
          <Reveal order={4}>
            <PersonWorkCard work={work} sub={`Every company they worked in, ${sinceLabel(detail.period, detail.since)}`} />
          </Reveal>
        )}
      </div>
    );
  }

  return (
    <PageBody>
      <div className="mb-5 flex items-center gap-4">
        {person && <Avatar person={person} size={52} />}
        <div className="min-w-0 flex-1">
          <PageHead
            eyebrow="Usage · AI usage"
            title={title}
            sub={sub}
            actions={<Action variant="outline" onClick={() => navigate('/usage')}>Back to AI usage</Action>}
          />
        </div>
      </div>
      <div className="mb-5">
        <FilterChips
          tabs={PERIODS.map((p) => ({ id: p.id, label: p.label }))}
          active={period}
          onChange={(id) => setPeriod(id as UsagePeriod)}
          label="Usage period"
        />
      </div>
      {!loading && failed && (
        <div className="mb-5">
          <ErrorNote message="Could not load this person's usage." />
          <div className="mt-3"><Action size="sm" variant="outline" onClick={load}>Try again</Action></div>
        </div>
      )}
      {body}
    </PageBody>
  );
};

export default PersonUsagePage;
